import { AuthController } from "@flaminem/kyc-authentication";

import { env } from "@kyc-front-end/env";

import environmentsConfig from "../../../../environments.json";

type AuthMode = "keycloak_proxy" | "frontend";

// Environments and their public origins live in environments.json so the dev
// proxy, the Keycloak authority and the local production run cannot drift apart.
const devAuthorities = Object.fromEntries(
  Object.entries(environmentsConfig.environments).map(([key, { origin }]) => [
    key,
    { domain: origin, realm: environmentsConfig.keycloakRealm },
  ])
);

const envKey = env.environment ?? "kyc";

const authority = {
  dev: devAuthorities[envKey] ?? devAuthorities.kyc,
  local: {
    domain: "http://localhost:8080", // local keycloak
    realm: "spa-kyc",
  },
  prod: {
    domain: "", // relative path — same origin as the app
    realm: "kyc-proxy-prod",
  },
};

const selectedAuthority = import.meta.env.DEV ? authority.dev : authority.prod;

export let authController: AuthController;
export let resolvedAuthMode: AuthMode = "keycloak_proxy";

export async function initAuthController(): Promise<void> {
  const fetchedMode = await fetchAuthMode();
  const isLocalhostRemoteDev =
    window.location.origin.includes("localhost") && env.developmentMode === "remote-dev";
  const mode = isLocalhostRemoteDev ? "frontend" : fetchedMode;
  resolvedAuthMode = mode;
  authController = new AuthController(
    {
      authority: `${selectedAuthority.domain}/auth/realms/kyc`,
      clientId:
        mode === "frontend" && !isLocalhostRemoteDev ? "frontend-client" : selectedAuthority.realm,
      scope: "openid profile email",
      responseType: "code",
      accessTokenExpiringNotificationTimeInSeconds: 60,
      redirectUri: `${window.location.origin}/login/callback`,
      silentRedirectUri: `${window.location.origin}/login/callback`,
      automaticSilentRenew: true,
      onSignInCallback: (): void => {
        window.history.replaceState({}, document.title, window.location.pathname);
      },
    },
    mode === "frontend" ? "token" : "cookie"
  );
}

async function fetchAuthMode(): Promise<AuthMode> {
  const abortController = new AbortController();
  const timeout = setTimeout(() => abortController.abort(), 3000);
  try {
    // The deployment owns this file and can change the authentication mode at
    // any time, so a cached copy must never decide the mode: browsers that
    // stored it under the former one-year asset policy would otherwise keep
    // selecting the previous provider until the entry expires.
    const res = await fetch("/assets/features.json", {
      signal: abortController.signal,
      cache: "no-store",
    });
    if (!res.ok) throw new Error(`Auth configuration failed: ${res.status}`);
    const json = await res.json();
    return json.authentication_mode === "frontend" ? "frontend" : "keycloak_proxy";
  } catch (e) {
    console.error("fetchAuthMode error", e);
    return "keycloak_proxy";
  } finally {
    clearTimeout(timeout);
  }
}
